/**
 * Media API - Face Detection & Verification
 *
 * Face detection, matching, and verification endpoints.
 */

import { z } from 'zod'
import { FaceMatchStatusSchema, VerificationStatusSchema } from './common'

// ============================================================================
// POST /v1/media/:id/detect-faces
// ============================================================================

export const DetectFacesRequestSchema = z.object({
  id: z.string(),
  priority: z.enum(['HIGH', 'NORMAL', 'LOW']).optional()
})

export type DetectFacesRequest = z.infer<typeof DetectFacesRequestSchema>

export const DetectFacesResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    messageId: z.string(),
    priority: z.string()
  })
})

export type DetectFacesResponse = z.infer<typeof DetectFacesResponseSchema>

// ============================================================================
// GET /v1/media/:id/faces (List Faces)
// ============================================================================

export const ListFacesRequestSchema = z.object({
  id: z.string(),
  status: FaceMatchStatusSchema.optional(),
  identified_user_id: z.string().optional(),
  limit: z.coerce.number().min(1).max(100).optional(),
  offset: z.coerce.number().min(0).optional()
})

export type ListFacesRequest = z.infer<typeof ListFacesRequestSchema>

// Response type is defined by service layer

// ============================================================================
// PATCH /v1/faces/:id/confirm (Confirm Face)
// ============================================================================

export const ConfirmFaceRequestSchema = z.object({
  id: z.string()
})

export type ConfirmFaceRequest = z.infer<typeof ConfirmFaceRequestSchema>

// Response type is defined by service layer

// ============================================================================
// PATCH /v1/faces/:id/reject (Reject Face)
// ============================================================================

export const RejectFaceRequestSchema = z.object({
  id: z.string()
})

export type RejectFaceRequest = z.infer<typeof RejectFaceRequestSchema>

// Response type is defined by service layer

// ============================================================================
// DELETE /v1/face-references/:id (Delete Face Reference)
// ============================================================================

export const DeleteFaceReferenceRequestSchema = z.object({
  id: z.string()
})

export type DeleteFaceReferenceRequest = z.infer<typeof DeleteFaceReferenceRequestSchema>

export const DeleteFaceReferenceResponseSchema = z.object({
  message: z.string()
})

export type DeleteFaceReferenceResponse = z.infer<typeof DeleteFaceReferenceResponseSchema>

// ============================================================================
// GET /v1/media/verifications (List Verifications)
// ============================================================================

export const ListVerificationsRequestSchema = z.object({
  limit: z.coerce.number().min(1).max(100).optional(),
  offset: z.coerce.number().min(0).optional(),
  status: VerificationStatusSchema.optional()
})

export type ListVerificationsRequest = z.infer<typeof ListVerificationsRequestSchema>

export const VerificationItemSchema = z.object({
  id: z.string(),
  media: z.object({
    id: z.string(),
    thumbnailUrl: z.string().nullable(),
    capturedAt: z.date().nullable()
  }),
  faceDetection: z.object({
    id: z.string(),
    boundingBox: z.object({
      left: z.number(),
      top: z.number(),
      width: z.number(),
      height: z.number()
    })
  }),
  confidence: z.number(),
  status: z.enum(['PENDING', 'CONFIRMED', 'REJECTED', 'EXPIRED']),
  expiresAt: z.date(),
  createdAt: z.date()
})

export type VerificationItem = z.infer<typeof VerificationItemSchema>

export const ListVerificationsResponseSchema = z.object({
  message: z.string(),
  data: z.array(VerificationItemSchema),
  meta: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
    pendingCount: z.number()
  })
})

export type ListVerificationsResponse = z.infer<typeof ListVerificationsResponseSchema>

// ============================================================================
// PATCH /v1/media/verifications/:id/confirm (Confirm Verification)
// ============================================================================

export const ConfirmVerificationRequestSchema = z.object({
  id: z.string().min(1)
})

export type ConfirmVerificationRequest = z.infer<typeof ConfirmVerificationRequestSchema>

export const ConfirmVerificationResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    id: z.string(),
    status: z.literal('CONFIRMED'),
    confirmedAt: z.date()
  })
})

export type ConfirmVerificationResponse = z.infer<typeof ConfirmVerificationResponseSchema>

// ============================================================================
// PATCH /v1/media/verifications/:id/reject (Reject Verification)
// ============================================================================

export const RejectVerificationRequestSchema = z.object({
  id: z.string().min(1)
})

export type RejectVerificationRequest = z.infer<typeof RejectVerificationRequestSchema>

export const RejectVerificationResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    id: z.string(),
    status: z.literal('REJECTED'),
    rejectedAt: z.date()
  })
})

export type RejectVerificationResponse = z.infer<typeof RejectVerificationResponseSchema>
